import { useEnvironmentContext } from '@susu/custom-made/providers/hooks/useEnvironmentContext';
import useFabricContext from '@susu/custom-made/providers/hooks/useFabricContext';
import useOptionContext from '@susu/custom-made/providers/hooks/useOptionContext';
import useUserContext from '@susu/custom-made/providers/hooks/useUserContext';
import { Clients } from '@susu/custom-made/utils/constants';
import type {
  Monogram,
  MonogramColor,
  MonogramFontTypes,
  MonogramItem,
  Option,
} from '@susu/custom-made/utils/interfaces';
import { isMonogramDraftComplete } from '@susu/custom-made/utils/monogram';
import { MONOGRAM_TEST_IDS } from '@susu/custom-made/utils/testIds';
import { getImagePaths } from '@susu/custom-made/utils/utils';
import cn from 'classnames';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { useState } from 'react';

import { useI18N } from '@susu/i18n/client';
import Button, { ButtonSize, ButtonType } from '../../button/button';
import Panel, { PanelSize } from '../../panel/panel';
import TextInput, { TextBorderType } from '../../textInput/textInput';
import { Tile, TileVariant } from '../../tile/tile';

const HEADER =
  'sticky top-0 z-1 bg-white text-center text-lg font-medium leading-base tracking-[-0.3px] text-gray-800 px-40 pt-12 pb-14 md:text-xl md:leading-lg md:tracking-normal md:pt-28 md:pb-32 lg:text-base lg:leading-base lg:pt-28 lg:pb-30 2xl:text-xl 2xl:leading-lg 2xl:pt-28 2xl:pb-32';
const BACK_BUTTON = 'absolute left-0 top-1/2 -translate-y-1/2';
const FIELDS = 'flex flex-col gap-40 pt-20 pb-32 lg:gap-32 2xl:gap-24';
// 320px is the fixed control column at 1440 and 1920; the panel is too narrow for two
// columns below that, so the fields stack.
const FIELD_ROW = 'grid grid-cols-1 2xl:grid-cols-[minmax(0,1fr)_320px] 2xl:gap-16';
const FIELD_LABEL = 'text-sm leading-sm font-medium text-text lg:text-base lg:leading-base';
const DIVIDER = 'hidden border-t border-border 2xl:block';
const TILE_GRID = 'grid grid-cols-2 gap-8';
// The panel is a flex column: the footer reserves its own height, so the scroll area never
// has to hardcode a matching offset (it drifted when the footer's padding changed).
const LAYOUT = 'absolute inset-x-0 bottom-0 top-32 flex flex-col md:top-0';
const FOOTER = 'shrink-0 flex items-center w-full bg-white border-t border-gray-400 p-16 lg:p-20';
const FOOTER_BUTTON = 'w-full';

const FieldRow = ({
  label,
  isLabelCentered = true,
  hasWideLabelGap = true,
  children,
}: {
  label: string;
  isLabelCentered?: boolean;
  hasWideLabelGap?: boolean;
  children: ReactNode;
}) => (
  <div
    className={cn(
      FIELD_ROW,
      isLabelCentered ? '2xl:items-center' : '2xl:items-start',
      hasWideLabelGap ? 'gap-16 2xl:gap-x-16 2xl:gap-y-0' : 'gap-8 2xl:gap-x-16 2xl:gap-y-0',
    )}
  >
    <span className={FIELD_LABEL}>{label}</span>
    <div>{children}</div>
  </div>
);

const TileGrid = ({
  items,
  listTestId,
  selectedId,
  getTileTestId,
  getThumbnail,
  onSelect,
}: {
  items: (MonogramColor | MonogramFontTypes)[];
  listTestId: string;
  selectedId?: number;
  getTileTestId: (id: number) => string;
  getThumbnail: (item: MonogramColor | MonogramFontTypes) => {
    imageThumbnail: string;
    fallbackImageThumbnail: string;
  };
  onSelect: (id: number) => void;
}) => (
  <div className={TILE_GRID} data-testid={listTestId}>
    {items.map(item => {
      const { imageThumbnail, fallbackImageThumbnail } = getThumbnail(item);

      return (
        <Tile
          key={item.id}
          tileId={item.id}
          tileText={item.name}
          tileThumbnail={imageThumbnail}
          tileFallbackThumbnail={fallbackImageThumbnail}
          tileVariant={TileVariant.Bordered}
          isSelected={selectedId === item.id}
          testId={getTileTestId(item.id)}
          tileClick={onSelect}
        />
      );
    })}
  </div>
);

export const MonogramDetailsPanel = ({
  monogramData,
  monogramObj,
  selectedMonogramId,
  isMonogramDetailsShown,
  setIsMonogramDetailsShown,
  setMonogramObj,
  applyMonogram,
}: {
  monogramData: Option | undefined;
  monogramObj: Monogram;
  selectedMonogramId: number;
  isMonogramDetailsShown: boolean;
  setIsMonogramDetailsShown: Dispatch<SetStateAction<boolean>>;
  setMonogramObj: Dispatch<SetStateAction<Monogram>>;
  applyMonogram: () => void;
}) => {
  const { t } = useI18N();
  const [isError, setIsError] = useState<boolean>(false);
  const { client, product } = useUserContext();
  const { selectedFabric } = useFabricContext();
  const { hiddenOptionsAndValues } = useOptionContext();
  const { cdnDomain } = useEnvironmentContext();
  const isInStore = client === Clients.inStore;
  const selectedDraft = monogramObj[selectedMonogramId];
  const positionName = monogramData?.positions?.[selectedMonogramId]?.name ?? '';

  const scrollContainerClasses = 'flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-16 lg:px-20';

  const updateDraft = (patch: Partial<MonogramItem>) => {
    setMonogramObj(prev => ({ ...prev, [selectedMonogramId]: { ...prev[selectedMonogramId], ...patch } }));
  };

  const changeMonogramInitials = (text: string) => {
    const shirtFormat = /^[a-zA-Z0-9,./ ]+$/;
    const allFormat = /^[a-zA-Z0-9,./'& üûùúîíìïöôòóœøõáàâäãåæçèéêëÿźñ]+$/;
    const format = product === 'Shirt' || product === 'TuxedoShirt' ? shirtFormat : allFormat;
    const formatSpaces = /^\s+$/;
    if ((format.test(text) && !formatSpaces.test(text)) || !text.length) {
      setIsError(false);
    } else {
      setIsError(true);
    }

    updateDraft({ initials: text });
  };

  const closeMonogramDetails = () => {
    setIsMonogramDetailsShown(false);
    setMonogramObj({});
    setIsError(false);
  };

  const applyClick = () => {
    applyMonogram();
    closeMonogramDetails();
  };

  const getThumbnail = (item: MonogramColor | MonogramFontTypes) =>
    getImagePaths(item.imageUrlTemplate, item.imageName, selectedFabric?.layerImageName || '', cdnDomain, true);

  const colors = Object.values(monogramData?.colors ?? {})
    .filter(color => !hiddenOptionsAndValues?.monogramColors.includes(color.id))
    .sort((a: MonogramColor, b: MonogramColor) => a.sequenceNo - b.sequenceNo);
  const fontTypes = Object.values(monogramData?.fontTypes ?? {});

  return (
    <Panel
      isActive={isMonogramDetailsShown}
      closeClick={closeMonogramDetails}
      panelSize={PanelSize.Full}
      testIdSubfix="monogram-details"
      zIndex={22}
    >
      {isMonogramDetailsShown && (
        <div className={LAYOUT}>
          <div className={scrollContainerClasses}>
            <div className={HEADER} data-testid={MONOGRAM_TEST_IDS.DETAILS_TITLE}>
              {positionName}
              <div className={BACK_BUTTON}>
                <Button
                  onClick={closeMonogramDetails}
                  type={ButtonType.IconOnlyRound}
                  size={ButtonSize.Large}
                  testId={MONOGRAM_TEST_IDS.DETAILS_BACK_BTN}
                >
                  <i className="susu-icons" translate="no">
                    arrow_left
                  </i>
                </Button>
              </div>
            </div>

            <div className={FIELDS}>
              <FieldRow label={t('monogramDetails.textLabel')} hasWideLabelGap={false}>
                <TextInput
                  onChange={changeMonogramInitials}
                  placeholder={t('monogramDetails.initialsPlaceholder')}
                  textValue={selectedDraft?.initials ?? ''}
                  inputTestId={MONOGRAM_TEST_IDS.INITIALS_INPUT}
                  maxLength={monogramData?.initialsMaxLength}
                  charCounter={`${selectedDraft?.initials?.length ?? 0}/${monogramData?.initialsMaxLength}`}
                  textBorderType={TextBorderType.Square}
                  errorMessage={isError ? t('monogramDetails.initialsError') : undefined}
                />
              </FieldRow>

              <div className={DIVIDER} />

              <FieldRow label={t('monogramDetails.fontStyleLabel')}>
                <TileGrid
                  items={fontTypes}
                  listTestId={MONOGRAM_TEST_IDS.FONT_LIST}
                  selectedId={selectedDraft?.fontStyleId}
                  getTileTestId={MONOGRAM_TEST_IDS.getFontTileId}
                  getThumbnail={getThumbnail}
                  onSelect={fontStyleId => {
                    updateDraft({ fontStyleId });
                  }}
                />
              </FieldRow>

              <div className={DIVIDER} />

              <FieldRow label={t('monogramDetails.colorLabel')} isLabelCentered={false}>
                <TileGrid
                  items={colors}
                  listTestId={MONOGRAM_TEST_IDS.COLOR_LIST}
                  selectedId={selectedDraft?.colorId}
                  getTileTestId={MONOGRAM_TEST_IDS.getColorTileId}
                  getThumbnail={getThumbnail}
                  onSelect={colorId => {
                    updateDraft({ colorId });
                  }}
                />
              </FieldRow>
            </div>
          </div>

          <div className={cn(FOOTER, isInStore && 'pb-[46px] md:pb-40 lg:pb-20')}>
            <div className={FOOTER_BUTTON}>
              <Button
                isFullSize={true}
                onClick={applyClick}
                type={ButtonType.Primary}
                testId={MONOGRAM_TEST_IDS.APPLY_BTN}
                isDisabled={!isMonogramDraftComplete(selectedDraft, isError)}
              >
                {t('monogramDetails.apply')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Panel>
  );
};
